import { createTheme as createMuiTheme, Theme, ThemeOptions } from '@mui/material';
import merge from 'deepmerge';
import _ from 'lodash';

export type ThemeMode = 'light' | 'dark';

type ColorRole = 'primary' | 'secondary' | 'critical' | 'success';

export const getActionPalette = (mode: ThemeMode = 'light') => {
    const isDark = mode === 'dark';
    const primaryText = isDark ? '#8AB4F8' : '#0058CA';
    const primaryTextPressed = isDark ? '#AECBFA' : '#004299';
    const primaryTextDisabled = isDark ? '#5F7FAE' : '#81AEE8';
    const primaryBorder = isDark ? '#8AB4F8' : '#2A76D9';
    const primaryBorderPressed = isDark ? '#AECBFA' : '#004299';
    const primaryBorderDisabled = isDark ? '#3F5F8F' : '#81AEE8';
    const secondarySurface = isDark ? '#3D3E42' : '#D4D4D7';
    const secondaryHovered = isDark ? '#55565A' : '#AAABB0';
    const secondaryPressed = isDark ? '#6D6E72' : '#909097';
    const secondaryDisabled = isDark ? '#242A33' : '#F0F1F1';
    const secondaryBorder = isDark ? '#5F6368' : '#D4D4D7';
    const secondaryBorderPressed = isDark ? '#80868B' : '#909097';
    const secondaryBorderDisabled = isDark ? '#3C4043' : '#F0F1F1';
    const secondaryOn = isDark ? '#E8EAED' : '#0F101A';

    return {
        primary: {
            mui: {
                main: '#2A76D9',
                dark: '#004299',
                contrastText: '#FFFFFF',
            },
            surface: '#2A76D9',
            hovered: '#0058CA',
            pressed: '#004299',
            disabled: '#81AEE8',
            subduedHovered: '#2A76D91A',
            subduedPressed: '#2A76D94D',
            text: primaryText,
            textPressed: primaryTextPressed,
            textDisabled: primaryTextDisabled,
            onText: '#FFFFFF',
            icon: primaryText,
            iconDisabled: primaryTextDisabled,
            border: primaryBorder,
            borderPressed: primaryBorderPressed,
            borderDisabled: primaryBorderDisabled,
        },
        secondary: {
            mui: {
                main: secondarySurface,
                dark: secondaryPressed,
                contrastText: secondaryOn,
            },
            surface: secondarySurface,
            hovered: secondaryHovered,
            pressed: secondaryPressed,
            disabled: secondaryDisabled,
            text: secondaryOn,
            onText: secondaryOn,
            border: secondaryBorder,
            borderPressed: secondaryBorderPressed,
            borderDisabled: secondaryBorderDisabled,
        },
    };
};

export interface ThemePalette {
    background: {
        default: string;
    };
    surface: {
        getBase(colorRole: ColorRole): string;
        getHovered(colorRole: ColorRole): string;
        getPressed(colorRole: ColorRole): string;
        getDisabled(colorRole: ColorRole): string;
        getSubduedHovered(colorRole: ColorRole): string | undefined;
        getSubduedPressed(colorRole: ColorRole): string | undefined;
        default: string;
        subdued: string;
        hovered: string;
        pressed: string;
        selected: string;
        primary: string;
        primaryHovered: string;
        primaryPressed: string;
        primaryDisabled: string;
        primarySubduedHovered: string;
        primarySubduedPressed: string;
        secondary: string;
        secondaryHovered: string;
        secondaryPressed: string;
        secondaryDisabled: string;
        critical: string;
        criticalHovered: string;
        criticalPressed: string;
        criticalDisabled: string;
        criticalSubdued: string;
        criticalSubduedHovered: string;
        criticalSubduedPressed: string;
        success: string;
        successSecondary: string;
        successHovered: string;
        successPressed: string;
        successDisabled: string;
        successSubdued: string;
        successSubduedHovered: string;
        successSubduedPressed: string;
        highlight: string;
        highlight2: string;
        highlightSubdued: string;
        warning: string;
        warningSecondary: string;
        warningSubdued: string;
        warningSubduedAlt: string;
        decorative1: string;
        decorative1Subdued: string;
        decorative2: string;
        decorative2Subdued: string;
        decorative3: string;
        decorative3Subdued: string;
        decorative4: string;
        decorative4Subdued: string;
        interactive: string;
        interactiveDisabled: string;
        tableSubdued: string;
        nora: string;
        noraDark: string;
        noraGradient: string;
        noraFooter: string;
        noraDivider: string;
        growthHubPremium: string;
    };
    text: {
        getBase(colorRole: ColorRole): string | undefined;
        getPressed(colorRole: ColorRole): string | undefined;
        getDisabled(colorRole: ColorRole): string | undefined;
        getOn(colorRole: ColorRole): string | undefined;
        default: string;
        subdued: string;
        disabled: string;
        onSecondary: string;
        primary: string;
        primaryPressed: string;
        primaryDisabled: string;
        onPrimary: string;
        critical: string;
        criticalPressed: string;
        criticalDisabled: string;
        onCritical: string;
        onCriticalSubdued: string;
        success: string;
        successSecondary: string;
        successPressed: string;
        successDisabled: string;
        onSuccess: string;
        onSuccessSubdued: string;
        highlight: string;
        onHighlight: string;
        warning: string;
        onWarning: string;
        onWarningSubdued: string;
        onWarningSubduedAlt: string;
        decorative1: string;
        onDecorative1Subdued: string;
        decorative2: string;
        onDecorative2Subdued: string;
        decorative3: string;
        onDecorative3Subdued: string;
        decorative4: string;
        onDecorative4Subdued: string;
        interactive: string;
        interactiveHovered: string;
        interactivePressed: string;
        interactiveDisabled: string;
        placeHolder: string;
        link: string;
    };
    icon: {
        default: string;
        subdued: string;
        disabled: string;
        primary: string;
        onPrimary: string;
        tertiary: string;
        tertiaryDisabled: string;
        warning: string;
        onWarning: string;
        critical: string;
        criticalDisabled: string;
        onCritical: string;
        success: string;
        successDisabled: string;
        onSuccess: string;
        highlight: string;
        onHighlight: string;
        decorative1: string;
        decorative1Subdued: string;
        decorative2: string;
        decorative2Subdued: string;
        decorative3: string;
        decorative3Subdued: string;
        decorative4: string;
        decorative4Subdued: string;
        decorative4Bright: string;
        interactive: string;
        interactiveHovered: string;
        interactivePressed: string;
        interactiveDisabled: string;
        interactiveCritical: string;
        interactiveCriticalHovered: string;
        interactiveCriticalPressed: string;
        interactiveCriticalDisabled: string;
        focused: string;
    };
    border: {
        getBase(colorRole: ColorRole): string;
        getPressed(colorRole: ColorRole): string;
        getDisabled(colorRole: ColorRole): string;
        getSubdued(colorRole: ColorRole): string | undefined;
        default: string;
        subdued: string;
        hovered: string;
        primary: string;
        primaryPressed: string;
        primaryDisabled: string;
        onPrimary: string;
        secondary: string;
        secondaryPressed: string;
        secondaryDisabled: string;
        onSecondary: string;
        critical: string;
        criticalPressed: string;
        criticalDisabled: string;
        criticalSubdued: string;
        onCritical: string;
        success: string;
        successPressed: string;
        successDisabled: string;
        successSubdued: string;
        onSuccess: string;
        highlight: string;
        highlightSubdued: string;
        warning: string;
        warningSubdued: string;
        warningSubduedAlt: string;
        decorative1: string;
        decorative2: string;
        decorative3: string;
        decorative4: string;
        interactive: string;
        focused: string;
        dark: string;
    };
    divider: {
        default: string;
    };
}

export const createTheme: (options?: ThemeOptions, ..._args: Record<string, unknown>[]) => Theme = (
    options,
    ..._args
) => {
    const actionPalette = getActionPalette('light');
    const optionsWithDefaults = merge(
        {
            // Keep MUI palette available, including custom keys we augment via typings
            palette: {},
            resqPalette: {
                background: {
                    default: '#F7F7F7',
                },
                surface: {
                    getBase(colorRole: ColorRole): string {
                        return (this as any)[colorRole] as string;
                    },
                    getHovered(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Hovered`] as string;
                    },
                    getPressed(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Pressed`] as string;
                    },
                    getDisabled(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Disabled`] as string;
                    },
                    getSubduedHovered(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}SubduedHovered`];
                    },
                    getSubduedPressed(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}SubduedPressed`];
                    },
                    default: '#FFFFFF',
                    subdued: '#F0F1F1',
                    hovered: '#F7F7F7',
                    pressed: '#EAF1FB',
                    selected: '#EAF1FB',
                    primary: actionPalette.primary.surface,
                    primaryHovered: actionPalette.primary.hovered,
                    primaryPressed: actionPalette.primary.pressed,
                    primaryDisabled: actionPalette.primary.disabled,
                    primarySubduedHovered: actionPalette.primary.subduedHovered,
                    primarySubduedPressed: actionPalette.primary.subduedPressed,
                    secondary: actionPalette.secondary.surface,
                    secondaryHovered: actionPalette.secondary.hovered,
                    secondaryPressed: actionPalette.secondary.pressed,
                    secondaryDisabled: actionPalette.secondary.disabled,
                    critical: '#E13139',
                    criticalHovered: '#B3272E',
                    criticalPressed: '#881D23',
                    criticalDisabled: '#F28F94',
                    criticalSubdued: '#FDEDEE',
                    criticalSubduedHovered: '#E131391A',
                    criticalSubduedPressed: '#E131394D',
                    success: '#2A865C',
                    successSecondary: '#33A370',
                    successHovered: '#216B49',
                    successPressed: '#195037',
                    successDisabled: '#51BF8C',
                    successSubdued: '#E3F5ED',
                    successSubduedHovered: '#2A865C1A',
                    successSubduedPressed: '#2A865C4D',
                    highlight: '#7D60E6',
                    highlight2: '#0F101A',
                    highlightSubdued: '#F2EFFC',
                    warning: '#F6D06E',
                    warningSecondary: '#EB6A1E',
                    warningSubdued: '#FCF0D0',
                    warningSubduedAlt: '#FDE8DD',
                    decorative1: '#EB6A1E',
                    decorative1Subdued: '#FDEEE5',
                    decorative2: '#00A1A3',
                    decorative2Subdued: '#DBF6F6',
                    decorative3: '#7D60E6',
                    decorative3Subdued: '#F2EFFC',
                    decorative4: '#AB8D40',
                    decorative4Subdued: '#FCF0D0',
                    interactive: '#5693E1',
                    interactiveDisabled: '#C0D6F4',
                    tableSubdued: '#F7F7F7',
                    nora: '#8FA88B',
                    noraDark: '#2F3B34',
                    noraGradient:
                        'conic-gradient(from 124deg at 57.19% 134.3%, #6F935E 0deg, #485B40 360deg)',
                    noraFooter: '#2A3A49',
                    noraDivider: '#0F101A',
                    growthHubPremium: '#003149',
                },
                text: {
                    getBase(colorRole: ColorRole): string | undefined {
                        return (this as any)[colorRole];
                    },
                    getPressed(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Pressed`];
                    },
                    getDisabled(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Disabled`];
                    },
                    getOn(colorRole: ColorRole): string | undefined {
                        return (this as any)[`on${_.capitalize(colorRole)}`];
                    },
                    default: '#0F101A',
                    subdued: '#5D5E68',
                    disabled: '#909097',
                    onSecondary: actionPalette.secondary.onText,
                    primary: actionPalette.primary.text,
                    primaryPressed: actionPalette.primary.textPressed,
                    primaryDisabled: actionPalette.primary.textDisabled,
                    onPrimary: actionPalette.primary.onText,
                    critical: '#B3272E',
                    criticalPressed: '#881D23',
                    criticalDisabled: '#F28F94',
                    onCritical: '#FFFFFF',
                    onCriticalSubdued: '#0F101A',
                    success: '#216B49',
                    successSecondary: '#2A865C',
                    successPressed: '#195037',
                    successDisabled: '#51BF8C',
                    onSuccess: '#FFFFFF',
                    onSuccessSubdued: '#0F101A',
                    highlight: '#6248C5',
                    onHighlight: '#FFFFFF',
                    warning: '#705C29',
                    onWarning: '#0F101A',
                    onWarningSubdued: '#0F101A',
                    onWarningSubduedAlt: '#B45524',
                    decorative1: '#994613',
                    decorative2: '#00696B',
                    decorative3: '#6248C5',
                    decorative4: '#705C29',
                    interactive: '#0058CA',
                    interactiveHovered: '#004299',
                    interactivePressed: '#0F101A',
                    interactiveDisabled: '#909097',
                    placeHolder: '#909097',
                    link: '#005BD1',
                },
                icon: {
                    default: '#0F101A',
                    subdued: '#5D5E68',
                    disabled: '#909097',
                    primary: actionPalette.primary.icon,
                    onPrimary: actionPalette.primary.onText,
                    tertiary: actionPalette.primary.icon,
                    tertiaryDisabled: actionPalette.primary.iconDisabled,
                    warning: '#8D7434',
                    onWarning: '#0F101A',
                    critical: '#B3272E',
                    criticalDisabled: '#F28F94',
                    onCritical: '#FFFFFF',
                    success: '#216B49',
                    successDisabled: '#51BF8C',
                    onSuccess: '#FFFFFF',
                    highlight: '#7D60E6',
                    onHighlight: '#FFFFFF',
                    decorative1: '#EB6A1E',
                    decorative1Subdued: '#F4935D',
                    decorative2: '#00A1A3',
                    decorative2Subdued: '#00BEC0',
                    decorative3: '#7D60E6',
                    decorative3Subdued: '#B1A0F0',
                    decorative4: '#AB8D40',
                    decorative4Subdued: '#CAA74B',
                    decorative4Bright: '#F6D06E',
                    interactive: '#0058CA',
                    interactiveHovered: '#004299',
                    interactivePressed: '#0F101A',
                    interactiveDisabled: '#909097',
                    interactiveCritical: '#E13139',
                    interactiveCriticalHovered: '#B3272E',
                    interactiveCriticalPressed: '#881D23',
                    interactiveCriticalDisabled: '#ED646B',
                    focused: '#2A76D9',
                },
                border: {
                    getBase(colorRole: ColorRole): string {
                        return (this as any)[colorRole] as string;
                    },
                    getPressed(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Pressed`] as string;
                    },
                    getDisabled(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Disabled`] as string;
                    },
                    getSubdued(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Subdued`];
                    },
                    default: '#AAABB0',
                    subdued: '#D4D4D7',
                    hovered: '#909097',
                    primary: actionPalette.primary.border,
                    primaryPressed: actionPalette.primary.borderPressed,
                    primaryDisabled: actionPalette.primary.borderDisabled,
                    onPrimary: 'rgba(15, 16, 26, 0.3)',
                    secondary: actionPalette.secondary.border,
                    secondaryPressed: actionPalette.secondary.borderPressed,
                    secondaryDisabled: actionPalette.secondary.borderDisabled,
                    onSecondary: 'rgba(15, 16, 26, 0.3)',
                    critical: '#E13139',
                    criticalPressed: '#881D23',
                    criticalDisabled: '#F28F94',
                    criticalSubdued: '#ED646B',
                    onCritical: 'rgba(15, 16, 26, 0.3)',
                    success: '#2A865C',
                    successPressed: '#195037',
                    successDisabled: '#51BF8C',
                    successSubdued: '#33A370',
                    onSuccess: 'rgba(15, 16, 26, 0.3)',
                    warning: '#8D7434',
                    warningSubdued: '#AB8D40',
                    warningSubduedAlt: '#F4B184',
                    highlight: '#7D60E6',
                    highlightSubdued: '#9780EB',
                    decorative1: '#EB6A1E',
                    decorative2: '#00A1A3',
                    decorative3: '#7D60E6',
                    decorative4: '#AB8D40',
                    interactive: '#0058CA',
                    focused: '#2A76D9',
                    dark: '#5D5E68',
                },
                divider: {
                    default: '#D4D4D7',
                },
            },
        },
        options ?? {},
    );
    // Backfill custom palette entries used by MUI components (neutral, danger, payment)
    // so color="neutral"/"danger"/"payment" props keep working.
    const rp = (optionsWithDefaults as any).resqPalette as ThemePalette;
    const pal = (optionsWithDefaults as any).palette as any;
    pal.neutral = pal.neutral ?? {
        // a light neutral for backgrounds/borders
        main: rp.border.subdued ?? rp.border.default,
        contrastText: rp.text.default,
    };
    pal.danger = pal.danger ?? {
        main: rp.surface.critical,
        contrastText: rp.text.onCritical,
    };
    pal.payment = pal.payment ?? {
        main: rp.surface.successSecondary ?? rp.surface.success,
        contrastText: rp.text.onSuccess,
    };
    const baseTheme = createMuiTheme(optionsWithDefaults, ..._args);

    // Create custom shadows array (must maintain exact length of 25 elements)
    const customShadows = [...baseTheme.shadows] as typeof baseTheme.shadows;
    customShadows[1] = '0px 4px 8px 0px rgba(218, 218, 218, 0.25)';

    // Create final theme with all augmented properties
    const finalTheme = createMuiTheme(
        {
            ...baseTheme,
            shadows: customShadows,
        },
        ..._args,
    );

    return finalTheme;
};
